import { useEffect, useMemo, useRef } from 'react';
import { formatUnits } from 'viem';
import type { Connector } from 'wagmi';
import {
  useAccount,
  useBalance,
  useConnect,
  useDisconnect,
  useSwitchChain,
} from 'wagmi';
import { TARGET_CHAIN } from './config';
import { openWalletModal } from './walletModalStore';

export function shortenAddress(address: string) {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// De-duplicate connectors for the picker: EIP-6963 discovery can surface the
// same wallet as both the generic "injected" entry and a named one, so we drop
// the bare "injected" fallback whenever a concrete injected wallet is present.
export function pickConnectors(connectors: readonly Connector[]): Connector[] {
  const hasNamedInjected = connectors.some((c) => c.type === 'injected' && c.id !== 'injected');
  const seen = new Set<string>();
  const out: Connector[] = [];
  for (const c of connectors) {
    if (c.id === 'injected' && hasNamedInjected) continue;
    const key = c.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

export function useWallet() {
  const { address, isConnected, chainId } = useAccount();
  const { isPending } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching, error: switchChainError } = useSwitchChain();
  const { data: balance } = useBalance({ address });

  const wrongNetwork = isConnected && chainId !== TARGET_CHAIN.id;

  // 연결 직후 잘못된 체인이면 타깃 체인 전환을 주소당 1회 자동 시도.
  // 모바일에서는 헤더의 작은 배너를 찾기 어려우므로 연결하자마자
  // 지갑 프롬프트를 띄워 주는 편이 낫다. (거절하면 배너로 폴백)
  const autoSwitchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (isConnected && wrongNetwork && address && autoSwitchedFor.current !== address) {
      autoSwitchedFor.current = address;
      switchChain({ chainId: TARGET_CHAIN.id });
    }
    if (!isConnected) autoSwitchedFor.current = null;
  }, [isConnected, wrongNetwork, address, switchChain]);

  // opens the wallet picker (WalletModal) instead of blindly grabbing
  // connectors[0], so mobile/multi-wallet users can choose their wallet
  const connectWallet = () => {
    openWalletModal();
  };

  const balanceLabel = useMemo(() => {
    if (!balance) return null;
    return `${Number(formatUnits(balance.value, balance.decimals)).toFixed(4)} ${balance.symbol}`;
  }, [balance]);

  return {
    address,
    isConnected,
    isPending,
    isSwitching,
    wrongNetwork,
    // 전환 실패 사유 (예: WalletConnect 지갑이 테스트넷 전환/추가를 지원하지 않음)
    switchError: switchChainError ? String(switchChainError.message || switchChainError) : null,
    balanceLabel,
    error: null as string | null,
    connectWallet,
    disconnect,
    switchToTargetChain: () => switchChain({ chainId: TARGET_CHAIN.id }),
  };
}
